import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { IconBackground } from "@/components/ui/icon-background";
import { FloatingAccents } from "@/components/decorative";
import { Target, Settings, Gamepad2, Users, ChevronRight } from "lucide-react";
import { CurriculumSection } from "./CurriculumSection";

const features = [
  {
    icon: Target,
    titleKey: "features.feature1.title",
    descriptionKey: "features.feature1.description",
    gradient: "teal" as const,
  },
  {
    icon: Settings,
    titleKey: "features.feature2.title",
    descriptionKey: "features.feature2.description",
    gradient: "blue" as const,
  },
  {
    icon: Gamepad2,
    titleKey: "features.feature3.title",
    descriptionKey: "features.feature3.description",
    gradient: "orange" as const,
  },
  {
    icon: Users,
    titleKey: "features.feature4.title",
    descriptionKey: "features.feature4.description",
    gradient: "purple" as const,
  },
];

export const FeaturesPreview = () => {
  const { t } = useTranslation();
  const [isCurriculumExpanded, setIsCurriculumExpanded] = useState(false);

  const handleCurriculumToggle = () => {
    setIsCurriculumExpanded(!isCurriculumExpanded);
    
    if (!isCurriculumExpanded) {
      setTimeout(() => {
        const curriculumSection = document.getElementById("curriculum-section");
        if (curriculumSection) {
          curriculumSection.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  };

  return (
    <>
      <section className="relative py-16 md:py-24 px-4 bg-muted/30 overflow-hidden">
        <FloatingAccents position="both" />
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="font-clash text-3xl xs:text-4xl md:text-5xl font-bold text-center mb-12 text-foreground tracking-tight">
            {t("features.headline")}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
            {features.map((feature, index) => (
              <Card key={index}>
                <CardContent className="p-8 flex flex-col items-center text-center">
                  <IconBackground 
                    icon={feature.icon} 
                    gradient={feature.gradient}
                    size="sm"
                    className="mb-4"
                  />
                  <h3 className="font-clash text-xl font-semibold mb-3 text-foreground tracking-tight">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="text-base text-foreground/80 leading-relaxed tracking-wide font-medium">
                    {t(feature.descriptionKey)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16 text-center">
            <button
              onClick={handleCurriculumToggle}
              className="group inline-flex items-center gap-2 text-base text-muted-foreground hover:text-foreground transition-colors duration-300"
              aria-expanded={isCurriculumExpanded}
              aria-controls="curriculum-section"
            >
              <span>{t("curriculum.trigger")}</span>
              <ChevronRight
                className={`w-5 h-5 transition-transform duration-300 ${
                  isCurriculumExpanded ? "rotate-90" : ""
                }`}
                style={{
                  background: "linear-gradient(90deg, hsl(99, 98%, 40%), hsl(330, 100%, 60%), hsl(263, 52%, 52%))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              />
            </button>
          </div>
        </div>
      </section>

      <div id="curriculum-section">
        <CurriculumSection isExpanded={isCurriculumExpanded} />
      </div>
    </>
  );
};
